export interface AuthToken {
    token: string;
}

export interface Iap {
    iapId: string;
    iapName: string;
    iapMetadata: IapMetadataResponse[];
}

export interface IapRelationship {
    firstIapId: string;
    secondIapId: string;
    directionality: string;
}

export interface IapRelationshipWithNames {
    firstIapName: string;
    secondIapName: string;
    iapRelationship: IapRelationship;
}

export interface IapRelationshipDetails {
    firstIapId: string;
    firstIapName: string;
    secondIapId: string;
    secondIapName: string;
    directionality: string;
}

export enum Directionality {
    TWO_WAY = 'TWO_WAY',
    ONE_WAY_FIRST_IAP_CONSUMING_FROM_SECOND = 'ONE_WAY_FIRST_IAP_CONSUMING_FROM_SECOND',
    ONE_WAY_SECOND_IAP_CONSUMING_FROM_FIRST = 'ONE_WAY_SECOND_IAP_CONSUMING_FROM_FIRST',
}

export interface Iogl {
    iogId: string;
    fcId: string;
    ifoId: string;
    fulfillmentEnabled: boolean;
    inboundEnabled: boolean;
}

interface MetadataObject {
    key: string;
    value: string;
    type: 'STRING' | 'NUMERIC';
}

interface MetadataResponse {
    matchedMetadata: MetadataObject[];
}

interface IogMetadataResponse extends MetadataResponse {
    iogId: string;
}

interface IapMetadataResponse extends MetadataResponse {
    iapId: string;
}

export interface Iog {
    iogId: string;
    iogName: string;
    iogMetadata: IogMetadataResponse[];
}

export interface AvailabilityConsumerKey {
    marketplaceId: string;
    merchantCustomerId: string;
    fulfillmentProgramId: string;
    fulfillmentRegionId: string;
}

export interface AvailabilityConsumer {
    id: string;
    key: AvailabilityConsumerKey;
}

export interface ProgramResponse {
    description: string;
    isActive: boolean;
    type: string;
}

export interface AvailabilityConsumerDetails {
    id: string;
    marketplaceId: string;
    merchantCustomerId: string;
    fulfillmentProgramId: string;
    fulfillmentRegionId: string;
}

export enum WorkflowExecutionStatus {
    NOT_STARTED = 'NOT_STARTED',
    RUNNING = 'RUNNING',
    SUCCEEDED = 'SUCCEEDED',
    FAILED = 'FAILED'
}

export enum WorkflowExecutionStatusLabel {
    NOT_STARTED = 'Not started',
    RUNNING = 'Running',
    SUCCEEDED = 'Succeeded',
    FAILED = 'Failed'
}

export interface WorkflowExecutionDetailsRequester {
    applicationName: string;
    actorId: string;
    actorType: string;
}

export interface WorkflowExecutionDetails {
    executionId: string;
    workflowName: string;
    status: WorkflowExecutionStatus;
    input: string;
    output: string;
    errorCode: string;
    errorMessage: string;
    createTime: Date;
    lastModifiedTime: Date;
    requester: WorkflowExecutionDetailsRequester;
    region: string | null;
}

export interface WorkflowExecution {
    executionId: string;
    workflowName: string;
    status: WorkflowExecutionStatus;
    startedAt: Date;
    lastModifiedAt: Date;
    startedBy: string;
    region: string;
}

export interface WorkflowExecutionHistory {
    executions: WorkflowExecution[];
    count: number;
}

export interface StartWorkflowResponse {
    executionId: string;
    errorName: string;
    errorMessage: string;
    success: boolean;
}

export interface WorkflowsResponse {
    workflows: WorkflowResponse[];
}

export interface WorkflowResponse {
    workflowName: string;
    version: number;
    regions: string[];
    tags: Record<string, string>;
    input: WorkflowInputSchema[];
    output: WorkflowOutputSchema[];
}

export interface WorkflowInputSchema {
    parameterName: string;
    parameterIdentifier?: string;
    type: string;
    prettyName: string;
    minSize: bigint;
    maxSize: bigint;
    exampleValue: string;
    optional: boolean;
}

export interface WorkflowOutputSchema {
    parameterName: string;
    parameterIdentifier?: string;
    type: string;
    prettyName: string;
    minSize: bigint;
    maxSize: bigint;
}

export enum WorkflowParameterType {
    BOOLEAN = 'BOOLEAN',
    INT = 'INT',
    LONG = 'LONG',
    STRING = 'STRING',
    LIST_LONG = 'LIST_LONG',
    LIST_STRING = 'LIST_STRING',
    MAP_LIST_STRING = 'MAP_LIST_STRING',
}

export interface ActionParameter {
    actionParameterName: string,
    actionParameterType: string,
}

export interface ActionResponse {
    actions: {
        [ActionName: string]: { input: ActionParameter[], output: ActionParameter[] }
    }
}

export interface ActionProps {
    actionName: string;
    actionParamNames: string[];
    actionParamValues: string[];
}

export interface AddSubmissionRequest {
    actions: ActionProps[];
    reference: string;
    approvers: string;
}

export interface AddSubmissionResponse {
    id: string
}

export interface SubmissionDetails {
    submissionId: string;
    status: string;
    author: string;
    creationDate: string;
    reference: string;
    approvers: string[];
    actions: any;
    errorMessage: string;
    canApprove: boolean;
    approversStr: string;
    actionProps: ActionProps[];
    canExecute: boolean;
    approverFromFrontend: string;
    actorId: string;
}

export interface GetPermissionsResponse {
    permissions: string[];
}

export interface Query {
    operation?: string;

    [propertyKey: string]: string[] | string | null | undefined;
}

export interface MetadataQuery extends Query {}

export interface InputQuery extends Query {
    inputOperation?: string
}

export interface OutputQuery extends Query {
    outputOperation?: string
}


export interface Key {
    key: string,
    type: string,
    owner?: string,
    visibility?: 'ALL' | 'OWNER'
}

export interface IogMetadataResult {
    iogId: string,

    [propertyKey: string]: string[] | string | undefined;
}

export interface IapMetadataQueryResponse {
    iapMetadata: IapMetadataResponse[]
    nextToken: string | null
}

export interface IogMetadataQueryResponse {
    iogMetadata: IogMetadataResponse[]
    nextToken: string | null
}

export interface IapMetadataResult {
    iapId: string,

    [propertyKey: string]: string[] | string | undefined;
}

export interface MetadataKey {
    key: string,
    type: 'STRING' | 'NUMERIC',
    owner?: string,
    visibility?: 'ALL' | 'OWNER',
    validationRegex?: string,
    allowedValues?: string[],
}

export interface IogMetadataKeyObject {
    iogMetadataKeyObject: MetadataKey
}

export interface IapMetadataKeyObject {
    iapMetadataKeyObject: MetadataKey
}

export interface DynamicExecutionsHistory {
    executions: DynamicExecution[],
}

export interface CheckDynamicExecutionResponse {
    execution: DynamicExecution
}

export interface DynamicExecution {
    executionId: string,
    status: WorkflowExecutionStatus,
    dryRun: boolean,
    version: number,
    source?: string,
    clientName: string,
    requester?: string,
    createTime: Date,
    lastModifiedTime: Date,
    input?: string, 
    rules: string[],
    errorCode: string;
    errorMessage: string;
    iapms?: IapmMappingChange[],
    iogls?: IoglMappingChange[],
}

export interface IapmMappingChange {
    iapId: string,
    iogId: string,
    fcId: string,
    priority: string | null,
    dynamicExecutionOperation: MappingOperation,
    mappingStatus: MappingStatus,
    ruleName: string,
}

export interface IoglMappingChange {
    iogId: string,
    fcId: string,
    ifoId: string,
    inboundEnabled: boolean | null,
    fulfillmentEnabled: boolean | null,
    dynamicExecutionOperation: MappingOperation,
    mappingStatus: MappingStatus,
    ruleName: string,
}

export interface SearchRuleGroupsResponse {
    ruleGroupNames: string[]
}

export interface CheckRuleGroupResponse {
    inheritedRuleGroups: RuleGroup[]
    childRuleGroups: string[]
    ruleGroup: RuleGroup
}

export interface SearchExclusionGroupsResponse {
    exclusionGroups: string[]
}

export interface CheckExclusionGroupResponse {
    exclusionGroup: string
}

export interface RuleGroup {
    name: string,
    businessIdentity: string,
    approvers: string[],
    exclusionGroups: string[],
    fcSelector?: string,
    rules?: Rule[],
}

export interface RuleGroupName {
    name: string,
}

export interface ExclusionGroupName {
    name: string,
}

export interface ExclusionGroupDefinition {
    [key: string]: string[]
}

export interface Rule {
    name: string,
    overrideSelector: string,
    fcSelector: string,
    mappings: string,
}

export enum MappingOperation {
    ADD = 'ADD',
    UPDATE = 'UPDATE',
    DELETE = 'DELETE',
}

export enum MappingStatus {
    SUCCESSFUL = 'SUCCESSFUL',
    FAILED = 'FAILED',
    REQUESTED = 'REQUESTED',
}

export interface DynamicHook {
    source: string,
    attribute: string,
}
